import React, { useId, useState } from 'react';
import styled from 'styled-components';
import DialogClearStyles, { DialogClearStylesButtonsFooter, DialogContent } from 'components/accounts/DialogClearStyles';
import { PrimaryButton, SecondaryButton } from 'components/v2/buttons';
import { HelpBox } from 'components/v2/help-boxes';
import { HeaderDialog } from 'styles/Styles.styled';
import MyRadioButton from 'components/controls/MyRadioButton';
import { ToolType } from 'types/tools/ToolType';
import { postCreateTool } from 'services/api/adminToolsApi';
import { toaster } from 'components/v2/toast';
import getToolTypeLabel from 'util/getToolTypeLabel';

interface Props {
  visible: boolean;
  onHide(): void;
  onCreated(toolId: string): void;
}

const ChooseToolTypeDialog: React.FC<Props> = ({ visible, onHide, onCreated }) => {
  return (
    <DialogClearStyles
      visible={visible}
      onHide={onHide}
      header={<HeaderDialog>Choose Tool type</HeaderDialog>}
      closable={false}
      draggable={false}
    >
      <DialogContent>
        <ToolTypeOptions onHide={onHide} onCreated={onCreated} />
      </DialogContent>
    </DialogClearStyles>
  );
};

export default ChooseToolTypeDialog;

const ToolTypeOptions: React.FC<{ onHide(): void; onCreated(toolId: string): void }> = ({ onHide, onCreated }) => {
  const [toolType, setToolType] = useState<ToolType>(ToolType.PREVERIFIED_LCA_TOOL);
  const [isCreating, setCreating] = useState<boolean>(false);

  const handleCreateTool = async () => {
    if (isCreating) {
      return;
    }

    setCreating(true);
    try {
      const toolId = await postCreateTool(toolType);
      onCreated(toolId);
      onHide();
    } catch (e) {
      if (e instanceof Error) {
        toaster({
          severity: 'error',
          summary: 'Failed to create tool',
          details: e.message,
        });
        console.error('Error creating tool', e);
      }

      setCreating(false);
    }
  };

  return (
    <>
      <HelpBox>Please select a Tool type to proceed</HelpBox>

      <OptionsList>
        <ToolOption toolType={ToolType.PREVERIFIED_LCA_TOOL} currentToolType={toolType} onChecked={setToolType} />
        <ToolOption toolType={ToolType.PREVERIFIED_EPD_TOOL} currentToolType={toolType} onChecked={setToolType} />
        <ToolOption toolType={ToolType.FULLY_PREVERIFIED_TOOL} currentToolType={toolType} onChecked={setToolType} />
      </OptionsList>

      <DialogFooter>
        <DialogClearStylesButtonsFooter>
          <SecondaryButton label="Cancel" onClick={() => onHide()} />
          <PrimaryButton label="Submit" onClick={handleCreateTool} loading={isCreating} />
        </DialogClearStylesButtonsFooter>
      </DialogFooter>
    </>
  );
};

const ToolOption = ({
  toolType,
  currentToolType,
  onChecked,
}: {
  toolType: ToolType;
  currentToolType: ToolType;
  onChecked(type: ToolType): void;
}) => {
  const id = useId();
  return (
    <RadioButtonWrapperLabel htmlFor={id}>
      <MyRadioButton
        inputId={id}
        value={toolType}
        name="toolType"
        checked={currentToolType === toolType}
        onChange={onChecked}
      />
      <span>{getToolTypeLabel(toolType)}</span>
    </RadioButtonWrapperLabel>
  );
};

const RadioButtonWrapperLabel = styled.label`
  background-color: rgba(231, 81, 19, 0.1);
  min-height: 44px;
  padding: 12px 15px;
  border-radius: 2px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${(props) => props.theme.colors.primaryBlack};
`;

const OptionsList = styled.div`
  display: grid;
  margin-top: 16px;
  gap: 8px;
`;

const DialogFooter = styled.div`
  margin-top: 30px;
`;
